/*
  Niwera Commerce Theme 1. Vanilla JS, no dependencies, loaded with defer.

  Markup contract (sections and snippets):
    [data-drawer="cart|menu|search"]   drawer root (.drawer), panel is .drawer__panel
    [data-drawer-open="name"]          opens that drawer
    [data-drawer-close]                closes the drawer it sits in
    [data-cart-region]                 cart drawer content, replaced from the Section Rendering API
    [data-cart-count]                  badge elements, text and [hidden] follow the cart count
    [data-line][data-line-key]         one cart line (drawer and cart page)
    [data-qty-step="-1|1"]             stepper button inside .qty, input is .qty input
    [data-line-remove]                 remove button inside a line
    [data-product-form]                add to cart form, [data-quick-add] on product cards
    [data-form-error]                  optional error slot inside a product form
    [data-carousel] > [data-carousel-track], [data-carousel-prev], [data-carousel-next]
    [data-search] > input[data-search-input] + [data-search-results]
    details[data-accordion]            smooth open/close
    details[data-footer-col]           open on desktop, collapsed on mobile
    select[data-auto-submit]           submits its form on change
    [data-reveal-item]                 below-the-fold fade-up, gated by html[data-reveal="true"]
*/
(() => {
  'use strict';

  const config = (() => {
    try {
      return JSON.parse(document.getElementById('theme-config').textContent);
    } catch (e) {
      return { cartType: 'drawer', routes: {}, strings: {} };
    }
  })();
  const { routes, strings } = config;
  const root = document.documentElement;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const isMobile = window.matchMedia('(max-width: 749px)');

  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

  const announce = (message) => {
    const el = document.getElementById('Announcer');
    if (!el || !message) return;
    el.textContent = '';
    // a second tick so repeated identical messages are read again
    setTimeout(() => (el.textContent = message), 50);
  };

  const debounce = (fn, wait) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /* Drawers ---------------------------------------------------------------- */
  let activeDrawer = null;
  let lastOpener = null;

  const openDrawer = (drawer, opener) => {
    if (!drawer) return;
    if (activeDrawer && activeDrawer !== drawer) closeDrawer(activeDrawer, false);
    lastOpener = opener || document.activeElement;
    drawer.classList.add('is-open');
    // inert also hides the closed drawer from assistive technology, so no aria-hidden is needed
    drawer.inert = false;
    root.classList.add('is-locked');
    activeDrawer = drawer;
    $$('[data-drawer-open]').forEach((b) =>
      b.setAttribute('aria-expanded', String(b.dataset.drawerOpen === drawer.dataset.drawer))
    );
    const panel = $('.drawer__panel', drawer);
    const target = $('[data-autofocus]', drawer) || panel;
    // wait a frame so the visibility transition has started and focus is accepted
    requestAnimationFrame(() => target && target.focus({ preventScroll: true }));
  };

  const closeDrawer = (drawer, restoreFocus = true) => {
    if (!drawer || !drawer.classList.contains('is-open')) return;
    drawer.classList.remove('is-open');
    drawer.inert = true;
    root.classList.remove('is-locked');
    $$('[data-drawer-open]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
    if (activeDrawer === drawer) activeDrawer = null;
    if (restoreFocus && lastOpener && document.contains(lastOpener)) lastOpener.focus({ preventScroll: true });
  };

  const drawerByName = (name) => $(`[data-drawer="${name}"]`);

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-drawer-open]');
    if (opener) {
      const name = opener.dataset.drawerOpen;
      // cart link falls back to the cart page when the theme is set to page mode
      if (name === 'cart' && (config.cartType !== 'drawer' || !drawerByName('cart'))) return;
      event.preventDefault();
      openDrawer(drawerByName(name), opener);
      return;
    }
    const closer = event.target.closest('[data-drawer-close]');
    if (closer) {
      event.preventDefault();
      closeDrawer(closer.closest('[data-drawer]'));
    }
  });

  // keep keyboard focus inside the open drawer (cookie banner, header and skip link sit outside it)
  document.addEventListener('focusin', (event) => {
    if (activeDrawer && !activeDrawer.contains(event.target)) $('.drawer__panel', activeDrawer).focus();
  });

  document.addEventListener('keydown', (event) => {
    if (!activeDrawer) return;
    if (event.key === 'Escape') {
      closeDrawer(activeDrawer);
      return;
    }
    if (event.key !== 'Tab') return;
    const items = $$(FOCUSABLE, activeDrawer).filter((el) => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === $('.drawer__panel', activeDrawer))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  /* Cart ------------------------------------------------------------------- */
  const setCartCount = (count) => {
    $$('[data-cart-count]').forEach((el) => {
      // badge spans live in the header, the region element carries the same attribute but stays untouched
      if (el.hasAttribute('data-cart-region')) return;
      el.textContent = count > 99 ? '99+' : String(count);
      el.hidden = count < 1;
    });
    $$('.header__cart').forEach((el) => el.setAttribute('aria-label', `${strings.cart || 'Cart'} (${count})`));
  };

  const nativeFetch = window.fetch.bind(window);
  let themeCartBusy = 0;

  const renderCart = (sectionHtml) => {
    if (!sectionHtml) return;
    const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const next = $('[data-cart-region]', doc);
    const current = $('[data-cart-region]');
    if (!next || !current) return;
    const active = document.activeElement;
    const key = active?.closest?.('[data-line-key]')?.dataset.lineKey;
    const control = key && (active.matches('[data-qty-step]') ? `[data-qty-step="${active.dataset.qtyStep}"]` : active.matches('[data-line-remove]') ? '[data-line-remove]' : '');
    current.innerHTML = next.innerHTML;
    const count = parseInt(next.getAttribute('data-cart-count') || '0', 10);
    current.setAttribute('data-cart-count', String(count));
    setCartCount(count);
    // re-rendering drops focus: put it back on the same control, or the panel when the line is gone
    const again = control && $(`[data-line-key="${CSS.escape(key)}"] ${control}`, current);
    if (again) again.focus();
    else if (key && activeDrawer) $('.drawer__panel', activeDrawer).focus();
    if (key) announce(strings.cartUpdated ? strings.cartUpdated.replace('__COUNT__', count) : '');
  };

  const cartRequest = async (url, body) => {
    // window.fetch so bundle apps can rewrite the add (tier quantity); the app hook below skips it
    themeCartBusy += 1;
    let res;
    try {
      res = await fetch(url, {
        method: 'POST',
        headers: body instanceof FormData ? { Accept: 'application/json' } : { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: body instanceof FormData ? body : JSON.stringify(body),
      });
    } finally {
      themeCartBusy = Math.max(0, themeCartBusy - 1);
    }
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.status) {
      throw new Error(data.description || data.message || strings.error || 'Error');
    }
    return data;
  };

  // third-party cart apps (AOV bundles) add via their own requests: re-render the drawer, open it after an add
  let appAdded = false;
  let countTimer;
  window.fetch = (input, init) => {
    const promise = nativeFetch(input, init);
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    // bundle apps block the theme's add and send their own (?app=...), so the theme promise may never settle
    const fromApp = /[?&]app=/.test(url);
    const match = (!themeCartBusy || fromApp) && url.match(/\/cart\/(add|update|change|clear)/);
    if (match) {
      if (match[1] === 'add') appAdded = true;
      promise.then(() => {
        if (fromApp && match[1] === 'add') {
          themeCartBusy = 0;
          $$('[data-product-form].is-busy').forEach((f) => {
            f.classList.remove('is-busy');
            const btn = f.querySelector('[type="submit"]');
            if (btn) {
              btn.classList.remove('is-loading');
              btn.removeAttribute('aria-busy');
            }
          });
        }
        clearTimeout(countTimer);
        countTimer = setTimeout(() => {
          const opened = appAdded;
          appAdded = false;
          refreshDrawer(opened);
        }, 150);
      }).catch(() => {});
    }
    return promise;
  };

  const refreshDrawer = async (open) => {
    const res = await nativeFetch(`${window.location.pathname}?sections=cart-drawer`).then((r) => r.json()).catch(() => null);
    if (!res) return;
    renderCart(res['cart-drawer']);
    if (open && config.cartType === 'drawer' && !onCartPage()) openDrawer(drawerByName('cart'));
  };
  // the AOV bundle app reloads the page after its add unless a known cart hook exists; this is the one it checks first
  window.monster_refresh = () => {
    refreshDrawer(true);
    return true;
  };

  const sectionParams = { sections: 'cart-drawer', sections_url: window.location.pathname };
  const onCartPage = () => !!$('[data-cart-page]');

  const changeLine = async (line, quantity) => {
    const key = line.dataset.lineKey;
    line.classList.add('is-updating');
    line.setAttribute('aria-busy', 'true');
    try {
      const data = await cartRequest(routes.cartChange, { id: key, quantity, ...sectionParams });
      if (onCartPage()) {
        window.location.reload();
        return;
      }
      renderCart(data.sections && data.sections['cart-drawer']);
    } catch (e) {
      // the cart may have changed anyway (Shopify clamps to stock): show what is really in it
      const res = await fetch(`${window.location.pathname}?sections=cart-drawer`).then((r) => r.json()).catch(() => null);
      if (res) renderCart(res['cart-drawer']);
      line.classList.remove('is-updating');
      line.removeAttribute('aria-busy');
      announce(e.message);
    }
  };

  document.addEventListener('click', (event) => {
    const step = event.target.closest('[data-qty-step]');
    if (step) {
      const qty = step.closest('.qty');
      const input = qty && $('input', qty);
      if (!input) return;
      event.preventDefault();
      const min = parseInt(input.min || '0', 10);
      const max = input.max ? parseInt(input.max, 10) : Infinity;
      const next = Math.min(max, Math.max(min, (parseInt(input.value, 10) || 0) + parseInt(step.dataset.qtyStep, 10)));
      input.value = String(next);
      const line = step.closest('[data-line]');
      if (line) changeLine(line, next);
      return;
    }
    const remove = event.target.closest('[data-line-remove]');
    if (remove) {
      const line = remove.closest('[data-line]');
      if (!line) return;
      event.preventDefault();
      changeLine(line, 0);
    }
  });

  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-line] .qty input');
    if (!input) return;
    const line = input.closest('[data-line]');
    changeLine(line, Math.max(0, parseInt(input.value, 10) || 0));
  });

  /* Add to cart ------------------------------------------------------------ */
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-product-form]');
    if (!form || !routes.cartAdd) return;
    event.preventDefault();
    if (form.classList.contains('is-busy')) return;

    const submit = form.querySelector('[type="submit"]');
    const quick = form.hasAttribute('data-quick-add');
    const errorSlot = $('[data-form-error]', form);
    const drawerMode = config.cartType === 'drawer' && !!drawerByName('cart');

    form.classList.add('is-busy');
    if (submit) {
      submit.classList.add('is-loading');
      submit.setAttribute('aria-busy', 'true');
    }
    if (errorSlot) errorSlot.textContent = '';

    const body = new FormData(form);
    if (drawerMode) {
      body.set('sections', sectionParams.sections);
      body.set('sections_url', sectionParams.sections_url);
    }

    try {
      const data = await cartRequest(routes.cartAdd, body);
      if (!drawerMode) {
        window.location.href = routes.cart;
        return;
      }
      let drawerHtml = data.sections && data.sections['cart-drawer'];
      if (!drawerHtml) {
        const res = await nativeFetch(`${window.location.pathname}?sections=cart-drawer`).then((r) => r.json()).catch(() => null);
        drawerHtml = res && res['cart-drawer'];
      }
      renderCart(drawerHtml);
      announce(strings.added);
      if (quick && submit) {
        submit.classList.add('is-added');
        setTimeout(() => submit.classList.remove('is-added'), 1200);
        // quick add keeps the shopper browsing, the badge and the live region confirm it
        if (!isMobile.matches) openDrawer(drawerByName('cart'), submit);
      } else {
        openDrawer(drawerByName('cart'), submit);
      }
    } catch (e) {
      if (errorSlot) errorSlot.textContent = e.message;
      announce(e.message);
    } finally {
      form.classList.remove('is-busy');
      if (submit) {
        submit.classList.remove('is-loading');
        submit.removeAttribute('aria-busy');
      }
    }
  });

  /* Sticky header ---------------------------------------------------------- */
  const header = $('.header');
  if (header) {
    let ticking = false;
    const update = () => {
      header.classList.toggle('is-scrolled', window.scrollY > 8);
      ticking = false;
    };
    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(update);
        }
      },
      { passive: true }
    );
    update();
  }

  /* Accordion -------------------------------------------------------------- */
  document.addEventListener('click', (event) => {
    const summary = event.target.closest('details[data-accordion] > summary');
    if (!summary || reducedMotion.matches) return;
    const details = summary.parentElement;
    const panel = $('[data-accordion-panel]', details);
    if (!panel || typeof panel.animate !== 'function') return;
    event.preventDefault();
    if (details.dataset.animating) return;
    details.dataset.animating = 'true';

    const done = () => {
      delete details.dataset.animating;
      panel.style.overflow = '';
    };
    panel.style.overflow = 'hidden';

    if (details.open) {
      const start = panel.offsetHeight;
      const anim = panel.animate({ height: [`${start}px`, '0px'] }, { duration: 240, easing: 'cubic-bezier(0.2, 0, 0, 1)' });
      anim.onfinish = () => {
        details.open = false;
        done();
      };
      anim.oncancel = done;
    } else {
      details.open = true;
      const end = panel.offsetHeight;
      const anim = panel.animate({ height: ['0px', `${end}px`] }, { duration: 320, easing: 'cubic-bezier(0.2, 0, 0, 1)' });
      anim.onfinish = done;
      anim.oncancel = done;
    }
  });

  /* Footer columns --------------------------------------------------------- */
  const footerCols = $$('details[data-footer-col]');
  if (footerCols.length) {
    const sync = () => footerCols.forEach((d) => (d.open = !isMobile.matches));
    sync();
    isMobile.addEventListener('change', sync);
  }

  /* Carousel arrows -------------------------------------------------------- */
  $$('[data-carousel]').forEach((carousel) => {
    const track = $('[data-carousel-track]', carousel);
    const prev = $('[data-carousel-prev]', carousel);
    const next = $('[data-carousel-next]', carousel);
    if (!track) return;
    const update = () => {
      const max = track.scrollWidth - track.clientWidth - 2;
      if (prev) prev.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft >= max;
      carousel.classList.toggle('is-static', max <= 0);
    };
    const go = (dir) =>
      track.scrollBy({ left: dir * track.clientWidth * 0.8, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    if (prev) prev.addEventListener('click', () => go(-1));
    if (next) next.addEventListener('click', () => go(1));
    track.addEventListener('scroll', debounce(update, 60), { passive: true });
    window.addEventListener('resize', debounce(update, 120));
    update();
  });

  /* Predictive search ------------------------------------------------------ */
  $$('[data-search]').forEach((scope) => {
    const input = $('[data-search-input]', scope);
    const results = $('[data-search-results]', scope);
    if (!input || !results || !routes.predictiveSearch) return;
    let controller = null;

    const run = async () => {
      const q = input.value.trim();
      if (q.length < 2) {
        results.innerHTML = '';
        results.removeAttribute('aria-busy');
        return;
      }
      if (controller) controller.abort();
      controller = new AbortController();
      results.setAttribute('aria-busy', 'true');
      try {
        const url = `${routes.predictiveSearch}?q=${encodeURIComponent(q)}&section_id=predictive-search&resources[limit]=6&resources[limit_scope]=each`;
        const res = await fetch(url, { signal: controller.signal });
        if (!res.ok) throw new Error(String(res.status));
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const section = $('[data-predictive-results]', doc);
        results.innerHTML = section ? section.innerHTML : '';
        results.removeAttribute('aria-busy');
      } catch (e) {
        if (e.name === 'AbortError') return;
        results.innerHTML = '';
        results.removeAttribute('aria-busy');
      }
    };
    input.addEventListener('input', debounce(run, 250));
  });

  /* Auto-submit selects ---------------------------------------------------- */
  document.addEventListener('change', (event) => {
    const select = event.target.closest('select[data-auto-submit]');
    if (select && select.form) select.form.requestSubmit();
  });

  /* Country and language selector ------------------------------------------ */
  const closeLoc = (loc, restoreFocus) => {
    const toggle = $('[data-loc-toggle]', loc);
    const panel = toggle && document.getElementById(toggle.getAttribute('aria-controls'));
    if (!panel || panel.hidden) return;
    panel.hidden = true;
    toggle.setAttribute('aria-expanded', 'false');
    if (restoreFocus) toggle.focus();
  };

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-loc-toggle]');
    $$('[data-loc]').forEach((loc) => {
      if (!toggle || !loc.contains(toggle)) {
        if (!loc.contains(event.target)) closeLoc(loc, false);
      }
    });
    if (!toggle) return;
    const panel = document.getElementById(toggle.getAttribute('aria-controls'));
    const open = panel.hidden;
    panel.hidden = !open;
    toggle.setAttribute('aria-expanded', String(open));
    if (open) $('select', panel).focus();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const loc = event.target.closest && event.target.closest('[data-loc]');
    if (loc) closeLoc(loc, true);
  });

  /* Reveal on scroll ------------------------------------------------------- */
  if (root.dataset.reveal === 'true' && !reducedMotion.matches && 'IntersectionObserver' in window) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.remove('is-pending');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );
    $$('[data-reveal-item]').forEach((el) => {
      // only elements below the first screen are hidden, so nothing can stay invisible above the fold
      if (el.getBoundingClientRect().top < window.innerHeight) return;
      el.classList.add('is-pending');
      observer.observe(el);
    });
  }

  /* Theme editor hooks ----------------------------------------------------- */
  document.addEventListener('shopify:section:select', (event) => {
    const drawer = event.target.closest && event.target.closest('[data-drawer]');
    if (drawer) openDrawer(drawer);
  });
  document.addEventListener('shopify:section:deselect', (event) => {
    const drawer = event.target.closest && event.target.closest('[data-drawer]');
    if (drawer) closeDrawer(drawer, false);
  });
})();
